import { useCallback, useEffect, useMemo, useState } from "react";
import { useIdentity } from "@/auth/ClientAuthProvider";
import { useThread } from "@/hooks/useThread";

const key = (leadId: string) => `tfwp.lastReadAt.${leadId}`;
const EVENT = "tfwp:read";

function readLastRead(leadId: string): string {
  try {
    return localStorage.getItem(key(leadId)) ?? "";
  } catch {
    return "";
  }
}

/** Messages from the team the client has not opened yet (this device). */
export function useUnreadCount(): number {
  const { leadId } = useIdentity();
  const thread = useThread();
  const [lastRead, setLastRead] = useState(() => readLastRead(leadId));

  useEffect(() => {
    setLastRead(readLastRead(leadId));
    const sync = () => setLastRead(readLastRead(leadId));
    window.addEventListener(EVENT, sync);
    return () => window.removeEventListener(EVENT, sync);
  }, [leadId]);

  return useMemo(
    () => (thread.data ?? []).filter((m) => m.direction === "outbound" && (!lastRead || m.created_at > lastRead)).length,
    [thread.data, lastRead],
  );
}

/** Call from the Messages screen: everything up to now is read. */
export function useMarkThreadRead() {
  const { leadId } = useIdentity();
  return useCallback(
    (latest: string | undefined) => {
      if (!latest) return;
      try {
        if (readLastRead(leadId) >= latest) return;
        localStorage.setItem(key(leadId), latest);
        window.dispatchEvent(new Event(EVENT));
      } catch {
        /* storage blocked */
      }
    },
    [leadId],
  );
}
